import { z } from "zod";
import {
  JOURNAL_SECTION_MAX,
  journalSectionKeySchema,
} from "./meeting-journal";

// TES-1019 (Emelie, points 1–3) — the AI help inside ONE journal heading of
// the care meeting's note. Two actions, both offered per box as a draft,
// never written into the box by themselves:
//
//   summary  "Sammanfatta": the heading's own prefill lines (her answers,
//            already scoped to this heading) summarised factually, keeping
//            positive findings, deviations, relevant free text and medically
//            relevant negations, adding nothing.
//   compose  "Sammanställ": the text now in this box (the doctor's additions
//            and the prefill) written together into coherent text, within
//            this heading only.
//
// One box in, one box out: the request names one heading and carries only
// that heading's text, so nothing can move between headings.
//
// When the model is off, fails or is rejected by the guard, the answer is
// still a 200 with `source: "fallback"`: the deterministic text (the prefill
// lines, or the box unchanged) and the reason. The doctor is never shown an
// error and is never blocked.
// TES-1034 — whose note the heading belongs to. The care meeting's (TES-1019)
// and, since S7 gave it the same headings, the prescription transfer
// review's. The request and result shapes below are the same for both; the
// route names the parent.
export const JOURNAL_AI_PARENT_KINDS = ["meeting", "transfer_review"] as const;
export type JournalAiParentKind = (typeof JOURNAL_AI_PARENT_KINDS)[number];
export interface JournalSectionAiParent {
  kind: JournalAiParentKind;
  id: string;
}

export const JOURNAL_AI_MODES = ["summary", "compose"] as const;
export const journalAiModeSchema = z.enum(JOURNAL_AI_MODES);
export type JournalAiMode = z.infer<typeof journalAiModeSchema>;

export const journalSectionAiRequestSchema = z
  .object({
    sectionKey: journalSectionKeySchema,
    mode: journalAiModeSchema,
    // compose only: the box's text as it stands on screen.
    text: z.string().max(JOURNAL_SECTION_MAX).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.mode === "compose" && v.text === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["text"],
        message: "compose_needs_text",
      });
    }
  });
export type JournalSectionAiRequest = z.infer<
  typeof journalSectionAiRequestSchema
>;

export const JOURNAL_AI_FALLBACK_REASONS = [
  // Nothing to summarise or write together.
  "no_input",
  // The model did not answer.
  "provider_error",
  // The model answered with content the guard could not trace to the input,
  // and nothing clean remained once those sentences were removed (TES-1128).
  "rejected",
] as const;
export const journalAiFallbackReasonSchema = z.enum(
  JOURNAL_AI_FALLBACK_REASONS,
);
export type JournalAiFallbackReason = z.infer<
  typeof journalAiFallbackReasonSchema
>;

export const journalSectionAiResultSchema = z.object({
  sectionKey: journalSectionKeySchema,
  mode: journalAiModeSchema,
  // The draft to offer: the model's text, or the deterministic fallback.
  text: z.string(),
  source: z.enum(["ai", "fallback"]),
  fallbackReason: journalAiFallbackReasonSchema.nullable(),
  // The stored AI draft and its journal revision (`ai_draft`), when the
  // model's text is offered.
  draftId: z.string().nullable(),
  generatedAt: z.string().nullable(),
  // TES-1128 — set (> 0) when the guard removed sentences that carried a
  // figure or term the input did not contain, and `text` is the rest.
  // Optional, so an admin bundle from before it simply ignores it.
  removedSentences: z.number().int().positive().optional(),
});
export type JournalSectionAiResult = z.infer<
  typeof journalSectionAiResultSchema
>;
